import { DynamicForm } from "@shared/components/DynamicForm";
import { FormField, FormService } from "@shared/services/FormServices";
import { Button } from "@shared/ui/button";
import { UseFormReturn } from "react-hook-form";

export interface BulkBillFilters {
  consumerRange: string;
  runDateFrom: string | null;
  runDateTo: string | null;
  daysBetweenRuns: string | null;
  page: number;
}

interface BulkBillFiltersProps {
  filters: BulkBillFilters;
  onFiltersChange: (filters: BulkBillFilters) => void;
  onApplyFilters: (filters: BulkBillFilters) => void;
  initiators: string[];
  onClearFilters: () => void;
}

export const BulkBillFilters = ({
  filters,
  onFiltersChange,
  onApplyFilters,
  initiators,
  onClearFilters,
}: BulkBillFiltersProps) => {
  const FIELD_CLASSES = {
    container: "w-full mb-4",
    label: "block text-sm font-semibold text-gray-700 mb-2",
    input:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    select:
      "w-full h-11 px-3 border border-gray-300 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    textarea:
      "w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-200 transition-all bg-gray-50",
    error: "text-red-600 text-sm mt-1",
    helperText: "text-xs text-gray-500 mt-1",
  } as const;

  const createFormFields = (): FormField[] => [
    {
      name: "consumerRange",
      label: "Consumer Count Range",
      type: "select",
      required: false,
      placeholder: "All Ranges",
      options: [
        { label: "All Ranges", value: "all" },
        { label: "Small (<500)", value: "small" },
        { label: "Medium (500-1000)", value: "medium" },
        { label: "Large (>1000)", value: "large" },
      ],
      classes: FIELD_CLASSES,
    },

    // {
    //   name: "amount_min",
    //   label: "Billed Amount Range ($) - Min",
    //   type: "number",
    //   required: false,
    //   placeholder: "Min",
    //   classes: FIELD_CLASSES,
    // },
    // {
    //   name: "amount_max",
    //   label: "Billed Amount Range ($) - Max",
    //   type: "number",
    //   required: false,
    //   placeholder: "Max",
    //   classes: FIELD_CLASSES,
    // },
    {
      name: "run_date_start",
      label: "Run Date Range - From",
      type: "datepicker",
      required: false,
      // classes: FIELD_CLASSES,
    },
    {
      name: "run_date_end",
      label: "Run Date Range - To",
      type: "datepicker",
      required: false,
      // classes: FIELD_CLASSES,
    },
  ];

  // Create form instance
  const form: UseFormReturn<BulkBillFilters> =
    FormService.createForm<BulkBillFilters>({
      fields: createFormFields(),
      defaultValues: filters,
    });

  // Handle form submission (Apply Filters)
  const handleSubmit = (data: BulkBillFilters) => {
    const currentValues = form.getValues();
    onApplyFilters({ ...currentValues, page: 1 });
  };

  // Handle form reset (Clear Filters)
  const handleReset = () => {
    const defaultFilters: BulkBillFilters = {
      status: [],
      initiatedBy: "all",
      accuracyMin: 0,
      accuracyMax: 200,
      consumerRange: "all",
      billedAmountMin: 0,
      billedAmountMax: 1000000,
      runDateFrom: "",
      runDateTo: "",
      daysBetweenRuns: "all",
      page: 1, // Include page in default
    };

    form.reset(defaultFilters);
    onClearFilters();
  };

  // Custom footer with Apply and Clear buttons
  const renderCustomFooter = () => (
    <div className="flex justify-end gap-4 items-center pt-4 border-gray-200">
      <button
        type="button"
        onClick={handleReset}
        className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors"
      >
        Clear Filters
      </button>

      <Button
        type="submit"
        className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
      >
        Apply Filters
      </Button>
    </div>
  );

  return (
    <div className="space-y-4">
      <DynamicForm
        fields={createFormFields()}
        form={form}
        onSubmit={handleSubmit}
        onReset={handleReset}
        config={{
          showSubmitButton: false, // We're using custom footer
          showResetButton: false, // We're using custom footer
          layout: "vertical",
          spacing: "medium",
        }}
        gridColumns={3}
        // responsiveColumns={{ sm: 1, md: 2, lg: 2, xl: 2 }}
        className="space-y-6"
        renderCustomFooter={renderCustomFooter}
      />
    </div>
  );
};
